"use client";

import React from "react";

import type { PlayerRef } from "@remotion/player";
import { useCallback } from "react";
import type { Layer } from "../types/Editor";
import { TimelineScrubber } from "./TimelineScrubber";

interface TimelineProps {
  layers: Layer[];
  currentTime: number;
  scrollPosition: number;
  visibleDuration: number;
  videoDuration: number;
  zoom: number;
  timelineRef: React.RefObject<HTMLDivElement>;
  timelineContentRef: React.RefObject<HTMLDivElement>;
  onTimelineClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  onScroll: (e: React.UIEvent<HTMLDivElement>) => void;
  onLayerMouseDown: (e: React.MouseEvent, layer: Layer) => void;
  onResizeMouseDown: (
    e: React.MouseEvent,
    layer: Layer,
    handle: "intro" | "main-left" | "main-right" | "outro"
  ) => void;
  formatTime: (seconds: number) => string;
  playerRef: React.RefObject<PlayerRef>;
  onFrameChange: (frame: number) => void;
}

export function Timeline({
  layers,
  currentTime,
  scrollPosition,
  visibleDuration,
  videoDuration,
  zoom,
  timelineRef,
  timelineContentRef,
  onTimelineClick,
  onScroll,
  onLayerMouseDown,
  onResizeMouseDown,
  formatTime,
  playerRef,
  onFrameChange,
}: TimelineProps) {
  const innerContentRef = React.useRef<HTMLDivElement>(null);

  const handleTimelineClickInternal = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!innerContentRef.current || !playerRef.current || !videoDuration)
        return;

      const contentRect = innerContentRef.current.getBoundingClientRect();
      const clickX = e.clientX - contentRect.left;
      const clickPercentage = clickX / contentRect.width;
      const newTime = clickPercentage * videoDuration;
      const clampedTime = Math.max(0, Math.min(newTime, videoDuration));
      const frame = Math.floor(clampedTime * 30);
      playerRef.current.seekTo(frame);
    },
    [videoDuration, playerRef]
  );

  const timeRuler = useCallback(() => {
    if (videoDuration === 0) return [];

    let interval = 1;

    if (visibleDuration > 60) {
      interval = 10;
    } else if (visibleDuration > 30) {
      interval = 5;
    } else if (visibleDuration > 15) {
      interval = 2;
    } else if (visibleDuration > 5) {
      interval = 1;
    } else {
      interval = 0.5;
    }

    const marks = [];
    for (let time = 0; time <= videoDuration; time += interval) {
      marks.push({
        time,
        label: formatTime(time),
        position: (time / videoDuration) * 100,
      });
    }

    return marks;
  }, [videoDuration, visibleDuration, formatTime]);

  const getLayerColor = (layerType: string) => {
    switch (layerType) {
      case "highlight":
        return {
          intro: "bg-amber-100",
          main: "bg-amber-400",
          outro: "bg-amber-100",
          border: "border-amber-500/20",
          icon: "✨",
          textColor: "text-amber-900",
        };

      case "blur":
        return {
          intro: "bg-purple-100",
          main: "bg-purple-400",
          outro: "bg-purple-100",
          border: "border-purple-500/20",
          icon: "🌫️",
          textColor: "text-purple-900",
        };

      case "zoom":
        return {
          intro: "bg-indigo-100",
          main: "bg-indigo-400",
          outro: "bg-indigo-100",
          border: "border-indigo-500/20",
          icon: "🔍",
          textColor: "text-indigo-900",
        };

      default:
        return {
          intro: "bg-slate-100",
          main: "bg-slate-400",
          outro: "bg-slate-100",
          border: "border-slate-500/20",
          icon: "⭐",
          textColor: "text-slate-900",
        };
    }
  };

  const renderTimelineLayers = useCallback(() => {
    const trackHeight = 42;
    const trackGap = 3;
    const trackTypes = ["highlight", "blur", "zoom"] as const;
    const handleWidth = 6;

    return (
      <div className="absolute inset-0">
        <div className="flex flex-col gap-1 h-full justify-end px-0">
          {trackTypes.map((type) => (
            <div
              key={type}
              className="relative rounded-xl bg-white border border-slate-100 shadow-[inset_0_2px_4px_rgba(0,0,0,0.02)]"
              style={{ height: `${trackHeight}px` }}
            >
              {/* Track Label - Moved to the left gutter */}
              <div className="absolute -left-10 top-1/2 -translate-y-1/2 text-[10px] font-bold text-slate-300 uppercase tracking-widest text-center w-8">
                {type === "highlight" ? "H" : type === "blur" ? "B" : "Z"}
              </div>

              {layers
                .filter((layer) => layer.type === type)
                .map((layer) => {
                  const totalDuration =
                    layer.introDuration +
                    layer.mainDuration +
                    layer.outroDuration;

                  const layerStart = (layer.start / videoDuration) * 100;
                  const layerWidth = (totalDuration / videoDuration) * 100;

                  const introWidth = (layer.introDuration / totalDuration) * 100;
                  const mainWidth = (layer.mainDuration / totalDuration) * 100;
                  const outroWidth = 100 - introWidth - mainWidth;

                  const colors = getLayerColor(layer.type);

                  return (
                    <div
                      key={layer.id}
                      className={`absolute rounded-xl shadow-lg border ${colors.border} overflow-hidden group/layer cursor-grab active:cursor-grabbing`}
                      style={{
                        left: `${layerStart}%`,
                        width: `${layerWidth}%`,
                        top: "2px",
                        bottom: "2px",
                        transition: "none",
                      }}
                      onMouseDown={(e) => onLayerMouseDown(e, layer)}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Intro section */}
                      {layer.type !== "blur" && (
                        <div
                          className={`absolute inset-y-0 left-0 ${colors.intro} opacity-40`}
                          style={{ width: `${introWidth}%` }}
                        />
                      )}

                      {/* Drag Handles */}
                      <div
                        className="absolute inset-y-0 left-0 w-2 hover:w-3 bg-black/25 cursor-col-resize z-30 transition-all flex items-center justify-center group/handle"
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          onResizeMouseDown(e, layer, "intro");
                        }}
                      >
                        <div className="w-0.5 h-3 bg-white/60 rounded-full group-hover/handle:bg-white/90 transition-colors" />
                      </div>
                      <div
                        className="absolute inset-y-0 right-0 w-2 hover:w-3 bg-black/25 cursor-col-resize z-30 transition-all flex items-center justify-center group/handle"
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          onResizeMouseDown(e, layer, "outro");
                        }}
                      >
                        <div className="w-0.5 h-3 bg-white/60 rounded-full group-hover/handle:bg-white/90 transition-colors" />
                      </div>

                      {/* Main section */}
                      <div
                        className={`absolute inset-y-0 ${colors.main} shadow-[inset_0_1px_1px_rgba(255,255,255,0.4)]`}
                        style={{
                          left: layer.type === "blur" ? "0" : `${introWidth}%`,
                          width: layer.type === "blur" ? "100%" : `${mainWidth}%`,
                        }}
                      />

                      {/* Inner Handles for Intro/Outro transitions */}
                      {layer.type !== "blur" && (
                        <>
                          <div
                            className="absolute inset-y-0 w-2 -translate-x-1/2 hover:w-3 bg-black/20 cursor-col-resize z-30 transition-all flex items-center justify-center group/inner-handle"
                            style={{ left: `${introWidth}%` }}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              onResizeMouseDown(e, layer, "main-left");
                            }}
                          >
                            <div className="w-px h-3 bg-white/50 rounded-full group-hover/inner-handle:bg-white/80 transition-colors" />
                          </div>
                          <div
                            className="absolute inset-y-0 w-2 -translate-x-1/2 hover:w-3 bg-black/20 cursor-col-resize z-30 transition-all flex items-center justify-center group/inner-handle"
                            style={{ left: `${introWidth + mainWidth}%` }}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              onResizeMouseDown(e, layer, "main-right");
                            }}
                          >
                            <div className="w-px h-3 bg-white/50 rounded-full group-hover/inner-handle:bg-white/80 transition-colors" />
                          </div>
                        </>
                      )}

                      {/* Outro section */}
                      {layer.type !== "blur" && (
                        <div
                          className={`absolute inset-y-0 right-0 ${colors.outro} opacity-40`}
                          style={{ width: `${outroWidth}%` }}
                        />
                      )}

                      {/* Layer label with icon */}
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none px-2 overflow-hidden">
                        <span className={`text-[10px] font-bold ${colors.textColor} flex items-center gap-1.5 bg-white/40 px-2 py-0.5 rounded-lg backdrop-blur-sm shadow-sm truncate`}>
                          <span className="text-xs">{colors.icon}</span>
                          <span className="capitalize">{layer.type}</span>
                        </span>
                      </div>
                    </div>
                  );
                })}
            </div>
          ))}
        </div>
      </div>
    );
  }, [layers, videoDuration, onLayerMouseDown, onResizeMouseDown]);

  return (
    <div className="h-48 bg-white border border-slate-200 rounded-[24px] shadow-sm overflow-hidden flex flex-col">
      <div
        className="flex-1 overflow-x-auto overflow-y-hidden custom-scrollbar-h"
        ref={timelineRef}
        onScroll={onScroll}
        style={{ scrollbarGutter: "stable" }}
      >
        <div
          className="h-full relative"
          style={{ width: `${100 * zoom}%`, minWidth: "100%" }}
          ref={timelineContentRef}
        >
          {/* Internal consistency wrapper for all timed elements */}
          <div
            className="absolute inset-y-0 left-12 right-12 cursor-crosshair"
            ref={innerContentRef}
            onClick={handleTimelineClickInternal}
          >
            {/* Time ruler background area */}
            <div
              className="absolute top-0 left-0 right-0 h-8 bg-slate-50 border-b border-slate-100 select-none pointer-events-none"
            >
              <div className="absolute inset-0 overflow-hidden">
                {timeRuler().map((mark, i) => (
                  <div
                    key={`${mark.time}-${i}`}
                    className="absolute text-slate-400 whitespace-nowrap text-[10px] font-bold tabular-nums"
                    style={{
                      left: `${mark.position}%`,
                      transform: "translateX(-50%)",
                      top: "8px",
                    }}
                  >
                    {mark.label}
                    <div
                      className="absolute w-px bg-slate-200 left-1/2 -translate-x-1/2 rounded-full"
                      style={{ top: "14px", height: "8px" }}
                    />
                  </div>
                ))}
              </div>
            </div>

            <TimelineScrubber
              timelineRef={timelineRef}
              playerRef={playerRef}
              scrollPosition={scrollPosition}
              visibleDuration={visibleDuration}
              videoDuration={videoDuration}
              zoom={zoom}
              onFrameChange={onFrameChange}
            />

            <div className="pt-2 h-full">
              {renderTimelineLayers()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
